// @flow

import { compose } from 'redux';
import hoistNonReactStatics from 'hoist-non-react-statics';
import * as React from 'react';
import { withRouter as baseWithRouter } from 'react-router';

type QueryString = string;
type LocationKey = string;
type Action = 'PUSH' | 'REPLACE' | 'POP';

// cf. https://github.com/remix-run/react-router/blob/v3.2.6/docs/Glossary.md#location
export type Location<Query = {| +[key: string]: ?string |}, State = any> = {|
  pathname: string,
  search: QueryString,
  query: Query,
  state?: State,
  action: Action,
  key?: LocationKey,
  // XXX not documented props: private?
  basename: string,
  hash: string,
|};

// cf. https://github.com/remix-run/react-router/blob/v3.2.6/docs/Glossary.md#locationdescriptor
export type LocationDescriptor<Query = { ... }, State = { ... }> = {
  pathname: string,
  search?: string,
  query?: Query,
  state?: State, // loose typing to allow Location's props e.g. `const nextLoc = { ...prevLoc, pathname }`
  ...
};

type Route = {
  path: string,
  component: React$ComponentType<any>,
  childRoutes: Array<Route>,
  indexRoute: {
    to: Location<>,
    onEnter: () => void,
    ...
  },
  ...
};

export type Router<Query = {| +[key: string]: ?string |}, State = any> = {|
  createKey: () => string,
  createPath: (
    pathOrLoc: string | Location<Query, State>,
    query?: { [name: string]: string | Array<string> } | string
  ) => string,
  createHref: (
    pathOrLoc: string | Location<Query, State>,
    query?: { [name: string]: string | Array<string> } | string
  ) => string,
  createLocation: (location: { ... }, action?: string, key?: string) => string,
  isActive: (pathOrLoc: string, indexOnly: ?boolean) => boolean,
  listen: (listener: () => void) => () => void,
  listenBefore: (hook: () => void) => () => void,
  location: Location<Query, State>,
  params: { [key: string]: string, ... },
  go: (n: number) => any,
  goBack: () => any,
  goForward: () => any,
  routes: Array<Route>,
  setRouteLeaveHook: (
    route: React.Element<any>,
    hook: () => boolean
  ) => (prevState: { ... }) => any,
  transitionTo: (location: { ... }) => void,
  /**
   * @deprecated: `push` and `replace` will be replaced by `navigate`.
   */
  push: (pathOrLoc: string | LocationDescriptor<Query>) => void,
  /**
   * @deprecated: `push` and `replace` will be replaced by `navigate`.
   */
  replace: (pathOrLoc: string | LocationDescriptor<Query>) => void,
  getCurrentLocation: () => Location<Query, State>,
|};

export type Params = {|
  subject?:
    | 'domain'
    | 'hosting'
    | 'shs'
    | 'gandimail'
    | 'billing'
    | 'certificate'
    | 'migration'
    | 'feedback'
    | 'account'
    | 'api'
    | 'marketplace'
    | 'vps'
    // Standalone pages:
    | 'reseller-contact'
    | 'corporate-contact'
    | 'my-rights',
  category?: string,
  topic?: string,
  token?: string,
  campaignSlug?: string,
|};

export type WithRouterProps<
  P = Params,
  Query = {| +[key: string]: ?string |},
  State = any
> = {|
  router: Router<Query, State>,
  location: Location<Query>,
  params: P,
  route: React.Element<any>,
  routeParams: Partial<P>,
  navigate: (
    to: string | number,
    options?: {|
      replace?: boolean,
      state?: any,
    |}
  ) => void,
|};

function customWithRouter(Component: React.ComponentType<any>) {
  const Wrapper = (props: WithRouterProps<>) => {
    function navigate(
      to: string | number,
      options?: {|
        replace?: boolean,
        state?: any,
      |}
    ): void {
      if (typeof to === 'number') {
        props.router.go(to);
        return;
      }

      const location = options?.state
        ? { pathname: to, state: options.state }
        : to;

      if (options?.replace) {
        props.router.replace(location);
      } else {
        props.router.push(location);
      }
    }

    return <Component {...props} navigate={navigate} />;
  };
  return hoistNonReactStatics(Wrapper, Component);
}

/**
 * `withRouter` will be removed with react-router@6 and replaced by `useNavigate()` and `useParams`
 *
 * This custom version allow gradual migration.
 */
export const withRouter: <C: React.ComponentType<any>>(
  Component: React.ComponentType<any>
) => C = compose(baseWithRouter, customWithRouter);
